import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, query, where, orderBy,
  getDocs, updateDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { captureLocation, formatCoordinates, openInGoogleMaps } from '../utils/locationTracker';
import { listTerritories, listDivisions, listAreas } from '../services/orgStructureService';
import './OutletRegistration.css';

const EQUIPMENT_TYPES = [
  { value: 'keg_pump',      label: 'Keg Pump',      icon: '☕' },
  { value: 'draft_machine', label: 'Draft Machine',  icon: '🍺' },
  { value: 'both',          label: 'Both',           icon: '🔧' },
  { value: 'none',          label: 'None yet',       icon: '—'  },
];

const emptyForm = {
  outletName: '',
  territoryId: '',
  divisionId: '',
  areaId: '',
  address: '',
  contactPerson: '',
  contactPhone: '',
  alternatePhone: '',
  equipmentType: 'none',
  serialNumbers: '',
  notes: '',
};

function OutletRegistration({ user, onBack }) {
  const [view, setView]           = useState('list');   // 'list' | 'register' | 'detail'
  const [outlets, setOutlets]     = useState([]);
  const [selected, setSelected]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm]           = useState(emptyForm);
  const [editMode, setEditMode]   = useState(false);

  // Org structure, loaded once for the cascading picker
  const [territories, setTerritories] = useState([]);
  const [divisions, setDivisions]     = useState([]);
  const [areas, setAreas]             = useState([]);

  useEffect(() => { loadOutlets(); loadOrgStructure(); }, []);

  const loadOrgStructure = async () => {
    try {
      const [t, d, a] = await Promise.all([listTerritories(), listDivisions(), listAreas()]);
      setTerritories(t.filter(x => x.active));
      setDivisions(d.filter(x => x.active));
      setAreas(a.filter(x => x.active));
    } catch (e) {
      console.error('❌ Load org structure error:', e);
    }
  };

  const divisionsForTerritory = divisions.filter(d => d.territoryId === form.territoryId);
  const areasForDivision = areas.filter(a => a.divisionId === form.divisionId);

  const loadOutlets = async () => {
    setLoading(true);
    try {
      // Technicians see their own outlets; supervisors/admins see all
      const ref = collection(db, 'outlets');
      let q;
      if (user.role === 'technician') {
        q = query(ref, where('registeredById', '==', user.id), orderBy('createdAt', 'desc'));
      } else {
        q = query(ref, orderBy('createdAt', 'desc'));
      }
      const snap = await getDocs(q);
      setOutlets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('❌ Load outlets error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!form.outletName.trim()) { alert('Outlet name is required.'); return; }
    if (!form.territoryId || !form.divisionId || !form.areaId) { alert('Please select a Territory, Division, and Area.'); return; }
    if (!form.contactPhone.trim()) { alert('Contact phone is required.'); return; }

    setSaving(true);
    try {
      const location = await captureLocation(user, 'OutletRegistration', { action: 'register_outlet' });
      const territory = territories.find(t => t.id === form.territoryId);
      const division = divisions.find(d => d.id === form.divisionId);
      const area = areas.find(a => a.id === form.areaId);

      const data = {
        outletName:       form.outletName.trim(),
        territoryId:       territory.id,
        territoryName:     territory.name,
        divisionId:        division.id,
        divisionName:      division.name,
        areaId:            area.id,
        areaName:          area.name,
        address:          form.address.trim(),
        contactPerson:    form.contactPerson.trim(),
        contactPhone:     form.contactPhone.trim(),
        alternatePhone:   form.alternatePhone.trim(),
        equipmentType:    form.equipmentType,
        serialNumbers:    form.serialNumbers.trim(),
        notes:            form.notes.trim(),
        status:           'active',
        registeredById:   user.id,
        registeredByName: user.name,
        createdAt:        serverTimestamp(),
        updatedAt:        serverTimestamp(),
        location: location ? {
          latitude:  location.latitude,
          longitude: location.longitude,
          accuracy:  location.accuracy,
        } : null,
        serviceCount: 0,
        lastServiceAt: null,
      };

      if (editMode && selected) {
        await updateDoc(doc(db, 'outlets', selected.id), { ...data, createdAt: selected.createdAt });
        showToast('✅ Outlet updated!');
      } else {
        await addDoc(collection(db, 'outlets'), data);
        showToast('✅ Outlet registered!');
      }

      setForm(emptyForm);
      setEditMode(false);
      setSelected(null);
      setView('list');
      await loadOutlets();

    } catch (e) {
      console.error('❌ Register outlet error:', e);
      alert('❌ Failed to save outlet. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (outlet) => {
    if (!window.confirm(`Deactivate "${outlet.outletName}"? It will be hidden from active lists.`)) return;
    try {
      await updateDoc(doc(db, 'outlets', outlet.id), { status: 'inactive', updatedAt: serverTimestamp() });
      await loadOutlets();
      setView('list');
      showToast('⚠️ Outlet deactivated.');
    } catch (e) {
      console.error('❌ Deactivate error:', e);
    }
  };

  const openDetail = (outlet) => {
    setSelected(outlet);
    setView('detail');
  };

  const openEdit = (outlet) => {
    setForm({
      outletName:    outlet.outletName || '',
      // Legacy outlets (pre-org-structure demo data) won't have these IDs
      // at all — the picker just starts blank for them, which is correct:
      // there's nothing valid to pre-select, so re-pick fresh.
      territoryId:   outlet.territoryId || '',
      divisionId:    outlet.divisionId || '',
      areaId:        outlet.areaId || '',
      address:       outlet.address || '',
      contactPerson: outlet.contactPerson || '',
      contactPhone:  outlet.contactPhone || '',
      alternatePhone:outlet.alternatePhone || '',
      equipmentType: outlet.equipmentType || 'none',
      serialNumbers: outlet.serialNumbers || '',
      notes:         outlet.notes || '',
    });
    setSelected(outlet);
    setEditMode(true);
    setView('register');
  };

  const filtered = outlets.filter(o =>
    o.status !== 'inactive' &&
    (o.outletName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     (o.areaName || o.area)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     o.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getEquipmentInfo = (type) => EQUIPMENT_TYPES.find(e => e.value === type) || EQUIPMENT_TYPES[3];

  const showToast = (msg) => {
    document.querySelectorAll('.toast-notification').forEach(t => t.remove());
    const t = document.createElement('div');
    t.className = 'toast-notification';
    t.innerHTML = `<div class="toast-icon">🏪</div><div class="toast-message">${msg}</div>`;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3000);
  };

  const formatDate = (ts) => {
    if (!ts) return 'N/A';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ── REGISTER / EDIT FORM ─────────────────────────────────
  if (view === 'register') {
    return (
      <div className="outlet-page">
        <div className="outlet-header">
          <button onClick={() => { setView('list'); setEditMode(false); setForm(emptyForm); }} className="back-button">← Back</button>
          <h1>{editMode ? '✏️ Edit Outlet' : '🏪 Register Outlet'}</h1>
        </div>

        <div className="outlet-form-card">
          {/* Outlet Info */}
          <div className="form-section">
            <div className="form-section-title">📋 Outlet Details</div>

            <div className="form-group">
              <label>Outlet Name *</label>
              <input className="form-input" placeholder="e.g. Westlands Sports Bar" value={form.outletName}
                onChange={e => setForm({ ...form, outletName: e.target.value })} />
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>Territory *</label>
                <select className="form-input" value={form.territoryId}
                  onChange={e => setForm({ ...form, territoryId: e.target.value, divisionId: '', areaId: '' })}>
                  <option value="">Select territory...</option>
                  {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Division *</label>
                <select className="form-input" value={form.divisionId} disabled={!form.territoryId}
                  onChange={e => setForm({ ...form, divisionId: e.target.value, areaId: '' })}>
                  <option value="">{form.territoryId ? 'Select division...' : 'Pick a territory first'}</option>
                  {divisionsForTerritory.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>Area *</label>
                <select className="form-input" value={form.areaId} disabled={!form.divisionId}
                  onChange={e => setForm({ ...form, areaId: e.target.value })}>
                  <option value="">{form.divisionId ? 'Select area...' : 'Pick a division first'}</option>
                  {areasForDivision.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>

            {territories.length === 0 && (
              <div className="form-hint">
              </div>
            )}

            <div className="form-group">
              <label>Street Address</label>
              <input className="form-input" placeholder="e.g. Waiyaki Way, Westlands" value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>

          {/* Contact Info */}
          <div className="form-section">
            <div className="form-section-title">👤 Contact Information</div>

            <div className="form-group">
              <label>Contact Person</label>
              <input className="form-input" placeholder="Manager / owner name" value={form.contactPerson}
                onChange={e => setForm({ ...form, contactPerson: e.target.value })} />
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>Primary Phone *</label>
                <input className="form-input" type="tel" placeholder="07xx xxx xxx" value={form.contactPhone}
                  onChange={e => setForm({ ...form, contactPhone: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Alternate Phone</label>
                <input className="form-input" type="tel" placeholder="07xx xxx xxx" value={form.alternatePhone}
                  onChange={e => setForm({ ...form, alternatePhone: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Equipment */}
          <div className="form-section">
            <div className="form-section-title">⚙️ Equipment Installed</div>

            <div className="equipment-grid">
              {EQUIPMENT_TYPES.map(eq => (
                <button key={eq.value}
                  className={`equipment-chip ${form.equipmentType === eq.value ? 'eq-active' : ''}`}
                  onClick={() => setForm({ ...form, equipmentType: eq.value })}>
                  <span className="eq-icon">{eq.icon}</span>
                  <span>{eq.label}</span>
                </button>
              ))}
            </div>

            {form.equipmentType !== 'none' && (
              <div className="form-group" style={{ marginTop: 14 }}>
                <label>Serial Number(s)</label>
                <input className="form-input" placeholder="e.g. KP-2024-001, DM-2024-005"
                  value={form.serialNumbers}
                  onChange={e => setForm({ ...form, serialNumbers: e.target.value })} />
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="form-section">
            <div className="form-section-title">💬 Notes</div>
            <textarea className="form-input form-textarea" rows={3}
              placeholder="Any special notes about this outlet..."
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>

          <div className="gps-notice">
            📍 GPS location will be auto-captured when you save
          </div>

          <div className="form-actions-row">
            <button className="cancel-btn"
              onClick={() => { setView('list'); setEditMode(false); setForm(emptyForm); }}>
              Cancel
            </button>
            <button className="submit-btn" onClick={handleRegister} disabled={saving}>
              {saving
                ? <><span className="spinner-small"></span> Saving...</>
                : editMode ? '💾 Save Changes' : '🏪 Register Outlet'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── OUTLET DETAIL ────────────────────────────────────────
  if (view === 'detail' && selected) {
    const eq = getEquipmentInfo(selected.equipmentType);
    return (
      <div className="outlet-page">
        <div className="outlet-header">
          <button onClick={() => setView('list')} className="back-button">← Back</button>
          <h1>🏪 Outlet Detail</h1>
          <div className="detail-actions">
            <button className="edit-btn" onClick={() => openEdit(selected)}>✏️ Edit</button>
            {(user.role === 'supervisor' || user.role === 'admin') && (
              <button className="deactivate-btn" onClick={() => handleDeactivate(selected)}>🚫 Deactivate</button>
            )}
          </div>
        </div>

        <div className="detail-card">
          {/* Name & Status */}
          <div className="detail-hero">
            <div className="detail-outlet-icon">🏪</div>
            <div className="detail-hero-info">
              <h2>{selected.outletName}</h2>
              <div className="detail-area">📍 {selected.areaName || selected.area}{selected.divisionName ? ` (${selected.divisionName})` : ''}{selected.address ? ` · ${selected.address}` : ''}</div>
              <span className={`detail-status ${selected.status}`}>{selected.status === 'active' ? '🟢 Active' : '🔴 Inactive'}</span>
            </div>
          </div>

          {/* Info Grid */}
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Contact Person</span>
              <span className="detail-value">{selected.contactPerson || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Primary Phone</span>
              <a href={`tel:${selected.contactPhone}`} className="detail-value detail-phone">
                📞 {selected.contactPhone}
              </a>
            </div>
            {selected.alternatePhone && (
              <div className="detail-item">
                <span className="detail-label">Alternate Phone</span>
                <a href={`tel:${selected.alternatePhone}`} className="detail-value detail-phone">
                  📞 {selected.alternatePhone}
                </a>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">Equipment</span>
              <span className="detail-value">{eq.icon} {eq.label}</span>
            </div>
            {selected.serialNumbers && (
              <div className="detail-item">
                <span className="detail-label">Serial Numbers</span>
                <span className="detail-value mono">{selected.serialNumbers}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">Registered By</span>
              <span className="detail-value">{selected.registeredByName}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Registered On</span>
              <span className="detail-value">{formatDate(selected.createdAt)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Services Done</span>
              <span className="detail-value">{selected.serviceCount || 0}</span>
            </div>
          </div>

          {selected.notes && (
            <div className="detail-notes">
              <div className="detail-label">Notes</div>
              <p>{selected.notes}</p>
            </div>
          )}

          {selected.location && (
            <div className="detail-gps">
              <div className="detail-label">GPS Location</div>
              <div className="gps-row">
                <span className="gps-coords">
                  {formatCoordinates(selected.location.latitude, selected.location.longitude, selected.location.accuracy)}
                </span>
                <button className="map-action-btn"
                  onClick={() => openInGoogleMaps(selected.location.latitude, selected.location.longitude, selected.outletName)}>
                  🗺️ Open in Maps
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── OUTLET LIST ──────────────────────────────────────────
  return (
    <div className="outlet-page">
      <div className="outlet-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="outlet-header-text">
          <h1>🏪 Outlets</h1>
          <p className="outlet-subtitle">{filtered.length} active outlet{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="register-btn" onClick={() => { setForm(emptyForm); setEditMode(false); setView('register'); }}>
          + Register
        </button>
      </div>

      {/* Search */}
      <div className="search-bar">
        <span className="search-icon">🔍</span>
        <input
          className="search-input"
          placeholder="Search outlets, areas, contacts..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>
        )}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading outlets...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-outlets">
          <div className="empty-icon">🏪</div>
          <h3>{searchTerm ? 'No outlets found' : 'No outlets registered yet'}</h3>
          <p>{searchTerm ? 'Try a different search term' : 'Tap Register to add your first outlet'}</p>
          {!searchTerm && (
            <button className="add-first-btn"
              onClick={() => { setForm(emptyForm); setEditMode(false); setView('register'); }}>
              + Register First Outlet
            </button>
          )}
        </div>
      ) : (
        <div className="outlets-list">
          {filtered.map(outlet => {
            const eq = getEquipmentInfo(outlet.equipmentType);
            return (
              <div key={outlet.id} className="outlet-card" onClick={() => openDetail(outlet)}>
                <div className="outlet-card-left">
                  <div className="outlet-card-icon">{eq.icon}</div>
                </div>
                <div className="outlet-card-body">
                  <div className="outlet-card-name">{outlet.outletName}</div>
                  <div className="outlet-card-area">📍 {outlet.areaName || outlet.area}</div>
                  <div className="outlet-card-meta">
                    {outlet.contactPerson && <span>👤 {outlet.contactPerson}</span>}
                    <span>📞 {outlet.contactPhone}</span>
                  </div>
                </div>
                <div className="outlet-card-right">
                  <span className="outlet-eq-badge">{eq.label}</span>
                  <span className="outlet-services">{outlet.serviceCount || 0} services</span>
                  <span className="outlet-chevron">›</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OutletRegistration;
